import { resolveProductIds } from "../../../api/productIdClient";
import type {
  ProductIdResolveRequest,
  ProductIdResolveResponse,
} from "../../../api/productIdClient";
import type {
  DesignerConfigResponse,
  SceneCatalogResponse,
  SceneProductDetailResponse,
} from "../types";

export interface FetchCatalogOptions {
  /** Base URL of the deployment. Empty = same origin. */
  baseUrl?: string;
  /** Unity target name. When set, the response includes a viewer build. */
  target?: string;
  stage?: "Development" | "Staging" | "Production";
  /** Short language code (e.g. "en", "dk"). Unknown/blank falls back to primary server-side. */
  lang?: string;
  signal?: AbortSignal;
}

/**
 * One-shot fetch of the entire catalog + viewer build. Anonymous + rate-limited
 * server-side (AnonymousRead policy). Sent with credentials:"include" so the
 * dashboard cookie attaches when a logged-in author loads the demo URL — costs
 * nothing for anonymous embeds and avoids a re-auth dance for previews.
 */
export async function fetchCatalog(opts: FetchCatalogOptions): Promise<SceneCatalogResponse> {
  const params = new URLSearchParams();
  if (opts.target) params.set("target", opts.target);
  if (opts.stage) params.set("stage", opts.stage);
  if (opts.lang) params.set("lang", opts.lang);

  const base = (opts.baseUrl ?? "").replace(/\/+$/, "");
  const qs = params.toString();
  const url = `${base}/api/embed/scene-designer/catalog${qs ? "?" + qs : ""}`;

  const res = await fetch(url, {
    headers: { Accept: "application/json" },
    credentials: "include",
    signal: opts.signal,
  });
  if (!res.ok) {
    const text = await res.text().catch(() => "");
    throw new Error(`fetchCatalog failed: ${res.status} ${res.statusText} ${text}`);
  }
  return (await res.json()) as SceneCatalogResponse;
}

export interface FetchDesignerOptions {
  baseUrl?: string;
  designerId: string;
  /** Optional target/stage fallback — only used if the Designer row hasn't pinned one. */
  target?: string;
  stage?: "Development" | "Staging" | "Production";
  /** Dashboard-preview opt-in. Forwards `allowArchived=1` so archived
   *  Designers load (the public embed path 404s them). */
  allowArchived?: boolean;
  /** Short language code (e.g. "en", "dk"). Unknown/blank falls back to primary server-side. */
  lang?: string;
  signal?: AbortSignal;
}

/**
 * Resolved Designer config — what the SceneDesignerTool fetches when its URL
 * carries a designerId. Returns the catalog scoped to the Designer's series,
 * the layout toggles, the theme tokens, and the viewer build (pinned by the
 * Designer row, with the request's target/stage as fallback). Anonymous +
 * rate-limited, same model as the bare catalog endpoint.
 */
export async function fetchDesigner(opts: FetchDesignerOptions): Promise<DesignerConfigResponse> {
  const params = new URLSearchParams();
  if (opts.target) params.set("target", opts.target);
  if (opts.stage) params.set("stage", opts.stage);
  if (opts.allowArchived) params.set("allowArchived", "1");
  if (opts.lang) params.set("lang", opts.lang);

  const base = (opts.baseUrl ?? "").replace(/\/+$/, "");
  const qs = params.toString();
  const url = `${base}/api/embed/scene-designer/designer/${encodeURIComponent(opts.designerId)}${qs ? "?" + qs : ""}`;

  const res = await fetch(url, {
    headers: { Accept: "application/json" },
    credentials: "include",
    signal: opts.signal,
  });
  if (!res.ok) {
    const text = await res.text().catch(() => "");
    throw new Error(`fetchDesigner failed: ${res.status} ${res.statusText} ${text}`);
  }
  return (await res.json()) as DesignerConfigResponse;
}

export interface FetchProductDetailOptions {
  baseUrl?: string;
  productId: string;
  /** Short language code (e.g. "en", "dk"). Unknown/blank falls back to primary server-side. */
  lang?: string;
  signal?: AbortSignal;
}

/**
 * Fetches a product's surfaces + per-surface material options — what the
 * selection panel needs to offer material swaps on a placed instance. Anonymous,
 * same auth model as the catalog. Cheap enough to call lazily on each selection
 * (the caller caches by productId).
 */
export async function fetchProductDetail(opts: FetchProductDetailOptions): Promise<SceneProductDetailResponse> {
  const base = (opts.baseUrl ?? "").replace(/\/+$/, "");
  const qs = opts.lang ? `?lang=${encodeURIComponent(opts.lang)}` : "";
  const url = `${base}/api/embed/scene-designer/product/${encodeURIComponent(opts.productId)}${qs}`;

  const res = await fetch(url, {
    headers: { Accept: "application/json" },
    credentials: "include",
    signal: opts.signal,
  });
  if (!res.ok) {
    const text = await res.text().catch(() => "");
    throw new Error(`fetchProductDetail failed: ${res.status} ${res.statusText} ${text}`);
  }
  return (await res.json()) as SceneProductDetailResponse;
}

export interface FetchProductIdsOptions {
  baseUrl?: string;
  body: ProductIdResolveRequest;
  signal?: AbortSignal;
}

/**
 * Thin wrapper around the shared productIdClient — kept under this name so the
 * CartModal call site doesn't have to move. New callers should import
 * resolveProductIds directly from api/productIdClient.
 */
export async function fetchProductIds(opts: FetchProductIdsOptions): Promise<ProductIdResolveResponse> {
  return resolveProductIds(opts);
}
